/**
 * Authenticated Layout Route
 *
 * Layout route with route guard for protected pages.
 * Redirects unauthenticated users to /login with return URL.
 *
 * Uses two-layer auth guard:
 * 1. beforeLoad: catches unauthenticated access on subsequent navigations
 *    (router context is already up-to-date from previous render)
 * 2. Component-level guard: catches the initial load race condition where
 *    beforeLoad returns early during auth loading, then redirects once
 *    auth state resolves via useAuth() subscription
 */

import { useEffect } from "react";
import { createFileRoute, Outlet, redirect, useNavigate } from "@tanstack/react-router";
import { IconContext } from "@phosphor-icons/react";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { useAuth } from "../stores/authStore";
import { applyTheme, clearTheme } from "../lib/theme-engine";
import { THEMES } from "../lib/themes";
import { useThemeStore } from "../stores/themeStore";
import { useNoIndex } from "../hooks/useSEO";

export const Route = createFileRoute("/_authenticated")({
  beforeLoad: async ({ context, location }) => {
    const { auth } = context;

    // If auth is resolved and user is not authenticated, redirect immediately.
    // This handles subsequent navigations where context is already up-to-date.
    if (!auth.isLoading && !auth.isAuthenticated) {
      throw redirect({
        to: "/login",
        search: {
          redirect: location.href,
        },
      });
    }

    // If still loading, let the component handle the guard.
    // (beforeLoad won't re-run after context updates via router.invalidate)
  },
  component: AuthenticatedLayoutComponent,
});

function AuthenticatedLayoutComponent() {
  const { user, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const currentThemeId = useThemeStore((s) => s.currentThemeId);
  const duotoneIcons = useThemeStore((s) => s.duotoneIcons);

  // Every authenticated app page is noindex,nofollow — search engines must
  // never index the application (Story 30.2). Applied at the layout so no
  // individual route can forget it.
  useNoIndex();

  // Component-level guard: redirect when auth resolves to unauthenticated.
  // This catches the race condition where beforeLoad returns early during loading.
  // NOTE: We also check `user === null` because AuthProvider derives isAuthenticated
  // via useEffect (one-render lag behind the Zustand store). After token refresh,
  // isLoading becomes false but isAuthenticated stays false for one render cycle.
  // The `user` check prevents a false redirect during that gap.
  useEffect(() => {
    if (!isLoading && !isAuthenticated && !user) {
      navigate({
        to: "/login",
        search: { redirect: window.location.pathname },
      });
    }
  }, [isLoading, isAuthenticated, user, navigate]);

  // Apply the user's theme to the document root while authenticated, so the WHOLE
  // document inherits it — the app shell, cards, AND body-portaled dropdowns and
  // dialogs (which live outside the app subtree). Cleanup on unmount reverts to the
  // default theme for public pages; the persisted selection is kept so it
  // re-applies on the next login.
  useEffect(() => {
    const theme = THEMES.find((t) => t.id === currentThemeId);
    if (theme) {
      applyTheme(theme, document.documentElement);
    }
    return () => {
      clearTheme(document.documentElement);
    };
  }, [currentThemeId]);

  const safeUser = user ?? {
    id: "",
    email: "",
    emailVerified: false,
  };

  return (
    <IconContext.Provider value={{ weight: duotoneIcons ? "duotone" : "regular" }}>
      <div id="authenticated-root" className={`contents ${duotoneIcons ? "duotone-icons" : ""}`}>
        <DashboardLayout user={{ ...safeUser, emailVerified: safeUser.emailVerified ?? false }}>
          <Outlet />
        </DashboardLayout>
      </div>
    </IconContext.Provider>
  );
}
